import {ValidationError, NonJSONResponseError} from '../errors';

import type {Endpoints} from './types';
import {
    validateBreakoutsBody,
    validateBreakouts200,
    validateBreakoutsMessageBody,
    validateBreakoutsMessage200,
    validateBreakoutsMessage404,
    validateBreakoutsDisconnect200,
    validateBreakoutsEmpty200,
    validateBreakoutRequestGuestToken200,
    validateBreakoutRequestGuestToken404,
    validateBreakoutMoveParticipantsFromMainBody,
    validateBreakoutMoveParticipantsFromMain200,
    validateBreakoutMoveParticipantsFromMain403,
    validateBreakoutMoveParticipantsBody,
    validateBreakoutMoveParticipants200,
    validateBreakoutMoveParticipants403,
    validateGuestLeaveBreakout200,
    validateHostBreakoutBuzz200,
    validateHostClearBreakoutBuzz200,
} from './validation';

type BreakoutsResponse = {status: 200; data: Endpoints['breakouts']['200']};
export async function breakouts({
    fetcher,
    init,
    params,
    body,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['breakouts']['Params'];
    body: Endpoints['breakouts']['Body'];
    host: string;
}): Promise<BreakoutsResponse> {
    if (!validateBreakoutsBody(body)) {
        throw new ValidationError(validateBreakoutsBody.errors);
    }
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/breakouts`,
        {
            ...init,
            method: 'post',

            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakouts200(data)) {
                throw new ValidationError(validateBreakouts200.errors);
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type BreakoutsMap = Endpoints['breakouts'];

type BreakoutsMessageResponse =
    | {status: 200; data: Endpoints['breakouts_message']['200']}
    | {status: 404; data: Endpoints['breakouts_message']['404']};
export async function breakoutsMessage({
    fetcher,
    init,
    params,
    body,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['breakouts_message']['Params'];
    body: Endpoints['breakouts_message']['Body'];
    host: string;
}): Promise<BreakoutsMessageResponse> {
    if (!validateBreakoutsMessageBody(body)) {
        throw new ValidationError(validateBreakoutsMessageBody.errors);
    }
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/breakouts/message`,
        {
            ...init,
            method: 'post',

            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutsMessage200(data)) {
                throw new ValidationError(validateBreakoutsMessage200.errors);
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 404) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutsMessage404(data)) {
                throw new ValidationError(validateBreakoutsMessage404.errors);
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type BreakoutsMessageMap = Endpoints['breakouts_message'];

type BreakoutsDisconnectResponse = {
    status: 200;
    data: Endpoints['breakouts_disconnect']['200'];
};
export async function breakoutsDisconnect({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['breakouts_disconnect']['Params'];
    host: string;
}): Promise<BreakoutsDisconnectResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/breakouts/disconnect`,
        {
            ...init,
            method: 'post',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutsDisconnect200(data)) {
                throw new ValidationError(
                    validateBreakoutsDisconnect200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type BreakoutsDisconnectMap = Endpoints['breakouts_disconnect'];

type BreakoutsEmptyResponse = {
    status: 200;
    data: Endpoints['breakouts_empty']['200'];
};
export async function breakoutsEmpty({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['breakouts_empty']['Params'];
    host: string;
}): Promise<BreakoutsEmptyResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/breakouts/empty`,
        {
            ...init,
            method: 'post',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutsEmpty200(data)) {
                throw new ValidationError(validateBreakoutsEmpty200.errors);
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type BreakoutsEmptyMap = Endpoints['breakouts_empty'];

type BreakoutRequestGuestTokenResponse =
    | {status: 200; data: Endpoints['breakout_request_guest_token']['200']}
    | {status: 404; data: Endpoints['breakout_request_guest_token']['404']};
export async function breakoutRequestGuestToken({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['breakout_request_guest_token']['Params'];
    host: string;
}): Promise<BreakoutRequestGuestTokenResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/breakouts/${encodeURIComponent(params.breakoutUuid)}/request_guest_token`,
        {
            ...init,
            method: 'post',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutRequestGuestToken200(data)) {
                throw new ValidationError(
                    validateBreakoutRequestGuestToken200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 404) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutRequestGuestToken404(data)) {
                throw new ValidationError(
                    validateBreakoutRequestGuestToken404.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type BreakoutRequestGuestTokenMap =
    Endpoints['breakout_request_guest_token'];

type BreakoutMoveParticipantsFromMainResponse =
    | {
          status: 200;
          data: Endpoints['breakout_move_participants_from_main']['200'];
      }
    | {
          status: 403;
          data: Endpoints['breakout_move_participants_from_main']['403'];
      };
export async function breakoutMoveParticipantsFromMain({
    fetcher,
    init,
    params,
    body,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['breakout_move_participants_from_main']['Params'];
    body: Endpoints['breakout_move_participants_from_main']['Body'];
    host: string;
}): Promise<BreakoutMoveParticipantsFromMainResponse> {
    if (!validateBreakoutMoveParticipantsFromMainBody(body)) {
        throw new ValidationError(
            validateBreakoutMoveParticipantsFromMainBody.errors,
        );
    }
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/participants/breakout`,
        {
            ...init,
            method: 'post',

            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutMoveParticipantsFromMain200(data)) {
                throw new ValidationError(
                    validateBreakoutMoveParticipantsFromMain200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 403) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutMoveParticipantsFromMain403(data)) {
                throw new ValidationError(
                    validateBreakoutMoveParticipantsFromMain403.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type BreakoutMoveParticipantsFromMainMap =
    Endpoints['breakout_move_participants_from_main'];

type BreakoutMoveParticipantsResponse =
    | {status: 200; data: Endpoints['breakout_move_participants']['200']}
    | {status: 403; data: Endpoints['breakout_move_participants']['403']};
export async function breakoutMoveParticipants({
    fetcher,
    init,
    params,
    body,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['breakout_move_participants']['Params'];
    body: Endpoints['breakout_move_participants']['Body'];
    host: string;
}): Promise<BreakoutMoveParticipantsResponse> {
    if (!validateBreakoutMoveParticipantsBody(body)) {
        throw new ValidationError(validateBreakoutMoveParticipantsBody.errors);
    }
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/breakouts/${encodeURIComponent(params.breakoutUuid)}/participants/breakout`,
        {
            ...init,
            method: 'post',

            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutMoveParticipants200(data)) {
                throw new ValidationError(
                    validateBreakoutMoveParticipants200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 403) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateBreakoutMoveParticipants403(data)) {
                throw new ValidationError(
                    validateBreakoutMoveParticipants403.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type BreakoutMoveParticipantsMap =
    Endpoints['breakout_move_participants'];

type GuestLeaveBreakoutResponse = {
    status: 200;
    data: Endpoints['guestLeaveBreakout']['200'];
};
export async function guestLeaveBreakout({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['guestLeaveBreakout']['Params'];
    host: string;
}): Promise<GuestLeaveBreakoutResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/leavebreakout`,
        {
            ...init,
            method: 'post',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateGuestLeaveBreakout200(data)) {
                throw new ValidationError(validateGuestLeaveBreakout200.errors);
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type GuestLeaveBreakoutMap = Endpoints['guestLeaveBreakout'];

type HostBreakoutBuzzResponse = {
    status: 200;
    data: Endpoints['host_breakout_buzz']['200'];
};
export async function hostBreakoutBuzz({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['host_breakout_buzz']['Params'];
    host: string;
}): Promise<HostBreakoutBuzzResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/breakouts/${encodeURIComponent(params.breakoutUuid)}/breakoutbuzz`,
        {
            ...init,
            method: 'post',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateHostBreakoutBuzz200(data)) {
                throw new ValidationError(validateHostBreakoutBuzz200.errors);
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type HostBreakoutBuzzMap = Endpoints['host_breakout_buzz'];

type HostClearBreakoutBuzzResponse = {
    status: 200;
    data: Endpoints['host_clear_breakout_buzz']['200'];
};
export async function hostClearBreakoutBuzz({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['host_clear_breakout_buzz']['Params'];
    host: string;
}): Promise<HostClearBreakoutBuzzResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/conferences/${encodeURIComponent(params.conferenceAlias)}/breakouts/${encodeURIComponent(params.breakoutUuid)}/clearbreakoutbuzz`,
        {
            ...init,
            method: 'post',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateHostClearBreakoutBuzz200(data)) {
                throw new ValidationError(
                    validateHostClearBreakoutBuzz200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type HostClearBreakoutBuzzMap = Endpoints['host_clear_breakout_buzz'];
